import { mockSectionData, type MockSectionCustomerInput } from './mockSectionData.util';
import { coerceBhConnectionCount } from './bhConnectionCoerce.util';
import { WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE } from './whoWereReachingSnapshotDetect.util';

/** Aggregated program goal row from `vw_current_year_program_goals`. */
export interface ProgramGoalsAggregateRow {
  customer_report_survey_name: string;
  in_progress: number;
  scheduled: number;
  completed: number;
  refused: number;
  in_program: number;
}

const normalizeName = (value: string): string => value.trim().toLowerCase();

const findSurveyRow = (
  rows: ProgramGoalsAggregateRow[],
  matcher: (name: string) => boolean,
): ProgramGoalsAggregateRow | null => {
  for (const row of rows) {
    const name = row.customer_report_survey_name ?? '';
    if (matcher(normalizeName(name))) {
      return row;
    }
  }
  return null;
};

/**
 * Coerces raw program goal aggregate rows from snapshot JSON or BigQuery cells.
 * @param raw - Program goal rows.
 * @returns Typed aggregate rows with integer counts.
 */
export const coerceProgramGoalsAggregateRows = (
  raw: unknown,
): ProgramGoalsAggregateRow[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map(row => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const record = row as Record<string, unknown>;
      const name =
        typeof record.customer_report_survey_name === 'string'
          ? record.customer_report_survey_name
          : '';
      if (!name) {
        return null;
      }
      return {
        customer_report_survey_name: name,
        in_progress: coerceBhConnectionCount(record.in_progress),
        scheduled: coerceBhConnectionCount(record.scheduled),
        completed: coerceBhConnectionCount(record.completed),
        refused: coerceBhConnectionCount(record.refused),
        in_program:
          'in_program' in record
            ? coerceBhConnectionCount(record.in_program)
            : coerceBhConnectionCount(record.in_progress),
      };
    })
    .filter((row): row is ProgramGoalsAggregateRow => row !== null);
};

/**
 * Builds the harp-enrollment snapshot from program goal aggregates.
 * Missing or all-zero HARP rows persist an empty warehouse chart so preview
 * does not fall back to illustrative mock counts.
 * @param rows - Program goal aggregates for the customer.
 * @param customer - Report customer context.
 * @param reportingPeriod - Human-readable report period label.
 * @returns Warehouse snapshot; `statuses` is empty when no HARP counts match.
 */
export const buildHarpEnrollmentSnapshotFromRows = (
  rows: ProgramGoalsAggregateRow[],
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
): Record<string, unknown> => {
  const aggregates = coerceProgramGoalsAggregateRows(rows);
  const harpRow = findSurveyRow(aggregates, name => name.includes('harp'));
  const statuses = harpRow
    ? [
        { label: 'Undecided', value: harpRow.in_progress },
        { label: 'Enrolled in HARP', value: harpRow.completed },
        { label: 'Refused', value: harpRow.refused },
        { label: 'In Progress', value: harpRow.scheduled },
      ].filter(row => row.value > 0)
    : [];

  const mockBase = mockSectionData('harp-enrollment', customer, reportingPeriod);
  return {
    ...mockBase,
    snapshotSource: WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE,
    statuses,
  };
};

const GAP_SCREENINGS = ['A1C', 'Dental', 'Breast', 'Colon', 'Kidney'] as const;

/**
 * Builds the gap-closures snapshot from program goal aggregates.
 * Missing screening rows persist an empty warehouse matrix so preview
 * does not fall back to illustrative mock counts.
 * @param rows - Program goal aggregates for the customer.
 * @param customer - Report customer context.
 * @param reportingPeriod - Human-readable report period label.
 * @returns Warehouse snapshot; `matrix` is empty when no screening rows match.
 */
export const buildGapClosuresSnapshotFromRows = (
  rows: ProgramGoalsAggregateRow[],
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
): Record<string, unknown> => {
  const aggregates = coerceProgramGoalsAggregateRows(rows);
  const statuses = ['In Progress', 'Scheduled', 'Completed', 'Refused'];
  const matrix: Array<{ screening: string; values: number[] }> = [];
  for (const screening of GAP_SCREENINGS) {
    const row = findSurveyRow(aggregates, name =>
      normalizeName(name).includes(screening.toLowerCase()),
    );
    if (row) {
      matrix.push({
        screening,
        values: [row.in_progress, row.scheduled, row.completed, row.refused],
      });
    }
  }

  const mockBase = mockSectionData('gap-closures', customer, reportingPeriod);
  return {
    ...mockBase,
    snapshotSource: WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE,
    statuses,
    matrix,
  };
};

const HOME_VISIT_PARTNERS = ['MANNA', 'Scene'] as const;

/**
 * Builds the home-visits snapshot from program goal aggregates.
 * Missing partner rows persist an empty warehouse chart so preview
 * does not fall back to illustrative mock counts.
 * @param rows - Program goal aggregates for the customer.
 * @param customer - Report customer context.
 * @param reportingPeriod - Human-readable report period label.
 * @returns Warehouse snapshot; `rows` is empty when no partner rows match.
 */
export const buildHomeVisitsSnapshotFromRows = (
  rows: ProgramGoalsAggregateRow[],
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
): Record<string, unknown> => {
  const aggregates = coerceProgramGoalsAggregateRows(rows);
  const statuses = ['In Program', 'Completed', 'Refused'];
  const partnerRows: Array<{ partner: string; values: number[] }> = [];
  for (const partner of HOME_VISIT_PARTNERS) {
    const row = findSurveyRow(aggregates, name =>
      normalizeName(name).includes(partner.toLowerCase()),
    );
    if (row) {
      partnerRows.push({
        partner,
        values: [row.in_program, row.completed, row.refused],
      });
    }
  }

  const totals = statuses.map((_, index) =>
    partnerRows.reduce((sum, row) => sum + (row.values[index] ?? 0), 0),
  );

  const mockBase = mockSectionData('home-visits', customer, reportingPeriod);
  return {
    ...mockBase,
    snapshotSource: WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE,
    statuses,
    rows: partnerRows,
    totals,
  };
};
